import { Stack, Typography } from '@mui/material';
import { flatMap, keyBy, uniq } from 'lodash';
import { PropsWithChildren } from 'react';
import { SetOptional } from 'type-fest';

import { Accordion } from '@/components/Accordion/Accordion';
import { FormAwareMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareMultiSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { Markdown } from '@/components/Markdown/Markdown';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { EditorInner } from '@/components/TextEditor/TextEditor.utils';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { COLORS } from '@/styles/tokens/colors';
import { MemoPromptMemoPromptType } from '@/types/schema';

import {
  MemoPromptsQuery,
  useMemoPromptsQuery,
} from '../graphql/MemoPrompts.generated';
import {
  CreatableMemoPromptType,
  MEMO_PROMPT_CONFIG,
  TRUST_COMPARISON_DOCUMENT_LIMIT,
} from '../memos.constants';
import { CreateMemoModalFormShape } from './CreateMemoModal.types';
import { DocumentPicker } from './DocumentPicker';
import { useGenerateMemo } from './useGenerateMemo';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;

interface CreateMemoModalProps extends FormModalOptionalProps {
  householdId: string;
  memoPromptType: CreatableMemoPromptType;
}

function getDefaultMemoPromptForType(
  defaultMemoPrompts: MemoPromptsQuery['defaultMemoPrompts'] | undefined,
  memoPromptType: CreatableMemoPromptType
) {
  return defaultMemoPrompts?.find((defaultMemoPrompt) => {
    return defaultMemoPrompt.memoType === memoPromptType;
  });
}

function getDefaultValues(
  memoPromptType: CreatableMemoPromptType
): CreateMemoModalFormShape {
  return {
    memoName: MEMO_PROMPT_CONFIG[memoPromptType].title,
    roleSummaryOptionKeys: [],
    specificInstructions: '',
    documentIds: [],
  };
}

function getRoleSummarySelectOptions(
  roleSummaryPredicateOptions:
    | MemoPromptsQuery['roleSummaryPredicateOptions']
    | undefined
) {
  return (
    roleSummaryPredicateOptions?.map((predicateOption) => ({
      display: predicateOption.label,
      value: predicateOption.key,
    })) ?? []
  );
}

function getRolePredicates(
  roleSummaryPredicateOptions:
    | MemoPromptsQuery['roleSummaryPredicateOptions']
    | undefined,
  selectedOptionKeys: string[]
) {
  const predicateOptionsByKey = keyBy(roleSummaryPredicateOptions, 'key');

  return uniq(
    flatMap(selectedOptionKeys, (selectedOptionKey) => {
      return predicateOptionsByKey[selectedOptionKey]?.predicates ?? [];
    })
  );
}

function PromptMarkdownInner({ children }: PropsWithChildren) {
  return (
    <EditorInner
      sx={{
        // Override the markdown viewer's default white background so it blends
        // into the filled accordion body.
        background: COLORS.GRAY[50],
      }}
    >
      {children}
    </EditorInner>
  );
}

function CreateMemoModalInner({
  householdId,
  memoPromptType,
  onClose,
  ...formModalProps
}: CreateMemoModalProps) {
  const { formRef, handleSave } = useFormSaveHandler();
  const { control, formState, handleSubmit, reset, shouldBlockNavigation } =
    useFormContext<CreateMemoModalFormShape>();
  const { generateMemo, loading: isGeneratingMemo } = useGenerateMemo();
  const { data } = useMemoPromptsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const memoPromptConfig = MEMO_PROMPT_CONFIG[memoPromptType];
  const isTrustComparison =
    memoPromptType === MemoPromptMemoPromptType.TrustComparison;
  const isRoleSummary = memoPromptType === MemoPromptMemoPromptType.RoleSummary;

  const selectedDefaultMemoPrompt = getDefaultMemoPromptForType(
    data?.defaultMemoPrompts,
    memoPromptType
  );

  const closeModal = () => {
    reset(getDefaultValues(memoPromptType));
    onClose();
  };

  const onSubmit = handleSubmit((formData) => {
    const rolePredicates = isRoleSummary
      ? getRolePredicates(
          data?.roleSummaryPredicateOptions,
          formData.roleSummaryOptionKeys
        )
      : undefined;

    return generateMemo({
      householdID: householdId,
      memoType: memoPromptType,
      name: formData.memoName.trim(),
      rolePredicates,
      specificInstructions: formData.specificInstructions.trim() || undefined,
      documentIDs: isTrustComparison ? formData.documentIds : undefined,
    });
  });

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      heading={memoPromptConfig.createHeading}
      onClose={closeModal}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton
            loading={isGeneratingMemo}
            onClick={handleSave}
            disabled={isGeneratingMemo}
          >
            Generate memo
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <Stack spacing={2} useFlexGap>
          <Typography variant="body-xs">
            {memoPromptConfig.description}
          </Typography>
          <FormAwareTextInput<CreateMemoModalFormShape>
            control={control}
            fieldName="memoName"
            label="Memo name"
            required
          />
          {isTrustComparison && (
            <DocumentPicker
              control={control}
              householdId={householdId}
              maxDocuments={TRUST_COMPARISON_DOCUMENT_LIMIT}
            />
          )}
          {isRoleSummary && (
            <FormAwareMultiSelectInput<CreateMemoModalFormShape>
              control={control}
              fieldName="roleSummaryOptionKeys"
              label="Select roles"
              options={getRoleSummarySelectOptions(
                data?.roleSummaryPredicateOptions
              )}
              required
            />
          )}
          <FormAwareTextInput<CreateMemoModalFormShape>
            control={control}
            fieldName="specificInstructions"
            label="Specific instructions"
            placeholder="Optional, describe any specific instructions or areas of focus"
            multiline
            rows={4}
          />
          <Accordion variant="filled" title="View prompt" disableExpandedMargin>
            <Markdown
              markdown={selectedDefaultMemoPrompt?.promptText ?? ''}
              EditorInner={PromptMarkdownInner}
            />
          </Accordion>
        </Stack>
      </form>
    </FormModal>
  );
}

export function CreateMemoModal(props: CreateMemoModalProps) {
  const formMethods = useForm<CreateMemoModalFormShape>({
    defaultValues: getDefaultValues(props.memoPromptType),
  });

  return (
    <FormProvider {...formMethods}>
      <CreateMemoModalInner {...props} />
    </FormProvider>
  );
}
